import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import MainLayout from './layouts/MainLayout';
import Dashboard from './pages/Dashboard';
import Tickets from './pages/Tickets';
import Emails from './pages/Emails';
import Adressen from './pages/Adressen';
import Dokumente from './pages/Dokumente';
import Vorlagen from './pages/Vorlagen';
import Textbausteine from './pages/Textbausteine';
import Einstellungen from './pages/Einstellungen';
import BenutzerEinstellungen from './pages/BenutzerEinstellungen';
import MailKonten from './pages/MailKonten';
import SystemPrompts from './pages/SystemPrompts';
import Mandanten from './pages/Mandanten';
import BenutzerVerwaltung from './pages/BenutzerVerwaltung';
import WebhookVerwaltung from './pages/WebhookVerwaltung';
import ScheduledJobsVerwaltung from './pages/ScheduledJobsVerwaltung';
import MitteilungenVerwaltung from './pages/MitteilungenVerwaltung';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import ProjectForm from './pages/ProjectForm';
import TicketCalendar from './pages/TicketCalendar';
import Kalender from './pages/Kalender';
import AppointmentCalendar from './pages/AppointmentCalendar';
import Workflows from './pages/Workflows';
import DokumentDetail from './pages/DokumentDetail';
import DokumentNeu from './pages/DokumentNeu';
import EmailCompose from './pages/EmailCompose';
import Help from './pages/Help';
import Wissensdatenbank from './pages/Wissensdatenbank';
import RagHilfe from './pages/RagHilfe';
import PaperlessBrowser from './pages/PaperlessBrowser';
import Login from './pages/Login';
import Register from './pages/Register';
import AIChat from './pages/AIChat';
import Mitteilungen from './pages/Mitteilungen';
import QuickLinks from './pages/QuickLinks';
import Kunden from './pages/Kunden';
import FristenDashboard from './pages/FristenDashboard';
import AssetVerwaltung from './pages/AssetVerwaltung';
import Zeiterfassung from './pages/Zeiterfassung';
import Abrechnung from './pages/Abrechnung';

// Portal imports
import PortalLayout from './layouts/PortalLayout';
import PortalDashboard from './pages/portal/PortalDashboard';
import PortalDokumente from './pages/portal/PortalDokumente';
import PortalAuftraege from './pages/portal/PortalAuftraege';
import PortalNachrichten from './pages/portal/PortalNachrichten';
import PortalDokumenteAdmin from './pages/PortalDokumenteAdmin';
import PortalNachrichtenAdmin from './pages/PortalNachrichtenAdmin';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Dashboard />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/tickets"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Tickets />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/ai-chat"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <AIChat />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/kalender"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <TicketCalendar />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/mein-kalender"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Kalender />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/termine"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <AppointmentCalendar />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/workflows"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Workflows />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/emails"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Emails />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/emails/compose/new"
              element={
                <PrivateRoute>
                  <EmailCompose />
                </PrivateRoute>
              }
            />
            <Route
              path="/emails/compose/:id"
              element={
                <PrivateRoute>
                  <EmailCompose />
                </PrivateRoute>
              }
            />
            <Route
              path="/adressen"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Adressen />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/kunden"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Kunden />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/fristen"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <FristenDashboard />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/zeiterfassung"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Zeiterfassung />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/abrechnung"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Abrechnung />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/dokumente"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Dokumente />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/dokumente/neu"
              element={
                <PrivateRoute>
                  <DokumentNeu />
                </PrivateRoute>
              }
            />
            <Route
              path="/dokumente/:id"
              element={
                <PrivateRoute>
                  <DokumentDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal-dokumente"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <PortalDokumenteAdmin />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/portal-nachrichten"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <PortalNachrichtenAdmin />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/paperless"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <PaperlessBrowser />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/vorlagen"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Vorlagen />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/textbausteine"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Textbausteine />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Projects />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/projects/new"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <ProjectForm />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/projects/:id"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <ProjectDetail />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/projects/:id/edit"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <ProjectForm />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/benutzer-einstellungen"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <BenutzerEinstellungen />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/hilfe"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Help />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/wissensdatenbank"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Wissensdatenbank />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/wissensdatenbank/hilfe"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <RagHilfe />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/mitteilungen"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Mitteilungen />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/mitteilungen/:id"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Mitteilungen />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/quicklinks"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <QuickLinks />
                  </MainLayout>
                </PrivateRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/einstellungen"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Einstellungen />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/mail-konten"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <MailKonten />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/webhooks"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <WebhookVerwaltung />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/jobs"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <ScheduledJobsVerwaltung />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/system-prompts"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <SystemPrompts />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/mandanten"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <Mandanten />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/benutzer"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <BenutzerVerwaltung />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/mitteilungen"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <MitteilungenVerwaltung />
                  </MainLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/assets"
              element={
                <PrivateRoute>
                  <MainLayout>
                    <AssetVerwaltung />
                  </MainLayout>
                </PrivateRoute>
              }
            />

            {/* Portal Routes (für externe Mandanten) */}
            <Route
              path="/portal"
              element={
                <PrivateRoute>
                  <PortalLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<PortalDashboard />} />
              <Route path="dokumente" element={<PortalDokumente />} />
              <Route path="auftraege" element={<PortalAuftraege />} />
              <Route path="nachrichten" element={<PortalNachrichten />} />
            </Route>
          </Routes>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
